import {
  scanListingSubmissionFreeText,
  type ListingFreeTextViolationCode,
  type ListingFreeTextViolationWithField,
} from "@/lib/listing/listingFreeTextPolicy";

/** Result of the listing lifecycle hard gate for title + description + defects. */
export type ListingFreeTextHardGateResult = {
  pass: boolean;
  violations: ListingFreeTextViolationWithField[];
  /** Distinct violation codes (stable for telemetry and auto-approve gates). */
  codes: ListingFreeTextViolationCode[];
  /** First violation message — suitable for admin summary or system_reject copy. */
  summary: string | null;
};

export function evaluateListingFreeTextHardGate(input: {
  title: string;
  description?: string | null;
  defects?: string | null;
}): ListingFreeTextHardGateResult {
  const violations = scanListingSubmissionFreeText(
    input.title ?? "",
    input.description ?? "",
    input.defects ?? "",
  );
  const codes = [...new Set(violations.map((v) => v.code))];

  return {
    pass: violations.length === 0,
    violations,
    codes,
    summary: violations[0]?.message ?? null,
  };
}

/** Anonymized violation-code counts for ops scripts and shadow metrics. */
export function tallyListingFreeTextViolationCodes(
  gates: readonly ListingFreeTextHardGateResult[],
): Partial<Record<ListingFreeTextViolationCode, number>> {
  const tally: Partial<Record<ListingFreeTextViolationCode, number>> = {};
  for (const gate of gates) {
    if (gate.pass) continue;
    for (const code of gate.codes) {
      tally[code] = (tally[code] ?? 0) + 1;
    }
  }
  return tally;
}

/** Plain-language admin one-liner when the hard gate fails. */
export function formatListingFreeTextHardGateAdminSummary(
  gate: ListingFreeTextHardGateResult,
): string | null {
  if (gate.pass) return null;
  const fields = [...new Set(gate.violations.map((v) => v.field))];
  const fieldLabel =
    fields.length === 1 ? fields[0] : `${fields.length} fields`;
  return `Content policy (${fieldLabel}): ${gate.codes.join(", ")}`;
}
